import {Button} from "@material-ui/core";
import 'Agent/AgentForms/Interactive/components/Interactive.sass';
import InteractiveDocument from "Agent/AgentForms/Interactive/components/InteractiveDocument";
import InteractiveSearchForm from "Agent/AgentForms/Interactive/components/InteractiveSearchForm";
import {
    changeInteractiveFilters,
    changeSort,
    clearInteractive,
    clearSort,
    getInteractiveFormData
} from "Agent/AgentForms/Interactive/ducks/Interactive";
import * as InteractiveStatesAction from "Agent/AgentForms/Interactive/ducks/interactiveFormState";
import {InteractiveFormStateDto} from "Agent/AgentForms/Interactive/models/Interactive";
import {StateFilter} from "Agent/AgentForms/Marketing/ducks/Marketing";
import {ModalState, showModal} from "Core/ducks/modal";
import {useBreadcrumbs} from 'Core/hooks/useBreadcrumbs';
import * as O from "fp-ts/lib/Option";
import React, {useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {RouteComponentProps} from 'react-router-dom';
import {bindActionCreators} from "redux";
import {ReducerState} from 'rootReducer';
import Column from 'Shared/components/PagedView/Column';
import {withSelectFilterProperties, withStringFilterProperties} from "Shared/components/PagedView/Filter";
import PagedView from 'Shared/components/PagedView/PagedView';
import {AgencyFilters} from 'Shared/models/AgencyFilters';
import {ActionsOf} from "Shared/utilities/actionCreator";
import {addFilterParameters} from "Shared/utilities/addFilterParameters";
import {withValueChangedHook} from "Shared/utilities/withValueChanged";
import {UserClaims} from 'Users/models/Claims';

type InteractiveProps = {
    selectedAgency: AgencyFilters;
    claims: UserClaims;
} & RouteComponentProps;

const InteractiveForm = (props: InteractiveProps) => {
    const dispatch = useDispatch();
    useBreadcrumbs("Interactive Forms", props.match.url);
    const {selectedAgency} = props;
    const [isSearchBlock, setIsSearchBlock] = useState(false);
    const {data, filter: interactiveFilters, sort, interactiveFormsCategories, interactiveFormsType} = useSelector((state: ReducerState) => state.interactive);
    const {getInteractiveFormStates} = bindActionCreators(InteractiveStatesAction as ActionsOf<typeof InteractiveStatesAction>, dispatch);
    const onViewStates = (interactiveFormStateDto: InteractiveFormStateDto) => () => {
        getInteractiveFormStates(interactiveFormStateDto);
        dispatch(showModal(ModalState.InteractiveFormStates({states: interactiveFormStateDto})));
    };

    const changeFilter = withValueChangedHook(interactiveFilters, changeInteractiveFilters, dispatch);

    const filterProperties = {
        changeFilter,
        filterData: interactiveFilters,
    };

    useEffect(() => {
        dispatch(changeInteractiveFilters({...interactiveFilters,}));
    }, []);

    useEffect(() => {
        interactiveFilters.pageNumber = "1";
        dispatch(
            getInteractiveFormData(props.claims, undefined, selectedAgency.isNewYork, interactiveFilters, O.toUndefined(sort))
        );
    }, [interactiveFilters, sort, selectedAgency.isNewYork]);

    useEffect(() => {
        interactiveFilters.pageNumber = "1";
        return () => {
            dispatch(clearInteractive({...interactiveFilters}));
            dispatch(clearSort());
        };
    }, [clearInteractive, changeInteractiveFilters]);

    const onClickSearchForm = () => {
        setIsSearchBlock(!isSearchBlock);
    }

    return (
        <>
            <div className="backdrop-container" id="backdrop" style={{display: isSearchBlock ? 'block' : 'none'}}/>
            <PagedView
                pagedViewKey="interactive"
                sort={O.toUndefined(sort)}
                onChangeSort={sort => dispatch(changeSort(sort))}
                loadMore={page => {
                    const nextPage: number = parseInt(interactiveFilters.pageNumber) + 1;
                    if (page.totalCount > page.result.length) {
                        interactiveFilters.pageNumber = nextPage.toString();
                        let sortValue = (sort._tag === 'Some') ? '&sortColumn=' + sort.value.column + '&sortOrder=' + sort.value.direction : '';
                        let link;
                        if (sortValue !== '') {
                            link = addFilterParameters(interactiveFilters) + '&isNewYork=' + selectedAgency.isNewYork + sortValue;
                        } else {
                            link = addFilterParameters(interactiveFilters) + '&isNewYork=' + selectedAgency.isNewYork + '&sortOrder=asc';
                        }
                        page.links = [{
                            "href": "api/documents/v1/interactiveForms" + link,
                            "method": "GET",
                            "rel": "nextpage",
                        }]
                    }
                    dispatch(
                        getInteractiveFormData(props.claims, page, selectedAgency.isNewYork, interactiveFilters)
                    );
                }}
                data={data}
                actions={<>
                    <Button
                        color="primary"
                        variant="contained"
                        onClick={onClickSearchForm}
                    > SEARCH FORMS
                    </Button>
                </>
                }
            >

                {columnProps => (
                    <>
                        <Column
                            {...columnProps}
                            title="Form Number"
                            sortColumnKey="formNumber"
                            filterProperties={withStringFilterProperties(filterProperties, 'formNumber')}
                            default
                        >
                            {interactive => interactive.formNumber}
                        </Column>
                        <Column
                            {...columnProps}
                            title="Category"
                            sortColumnKey="category"
                            filterProperties={withSelectFilterProperties(filterProperties, 'category', interactiveFormsCategories)}
                            default
                        >
                            {interactive => interactive.category}
                        </Column>
                        <Column
                            {...columnProps}
                            title="Form Type"
                            sortColumnKey="type"
                            filterProperties={withSelectFilterProperties(filterProperties, 'type', interactiveFormsType)}
                            default
                        >
                            {interactive => interactive.type}
                        </Column>
                        <Column
                            {...columnProps}
                            title="Product"
                            sortColumnKey="product"
                            filterProperties={withStringFilterProperties(filterProperties, 'product')}
                            default
                        >
                            {interactive => interactive.product}
                        </Column>
                        <Column
                            {...columnProps}
                            title="States"
                            sortColumnKey="states"
                            filterProperties={withSelectFilterProperties(filterProperties, 'states', StateFilter)}
                            default
                        >
                            {interactive =>
                                <Button
                                    id={`interactive-view-states-btn-${interactive.fId}`}
                                    className="primary-action-button"
                                    color="primary"
                                    onClick={onViewStates({fid: interactive.fId})}
                                >
                                    View
                                </Button>
                            }
                        </Column>
                        <Column
                            {...columnProps}
                            title="Description"
                            sortColumnKey="description"
                            filterProperties={withStringFilterProperties(filterProperties, 'description')}
                            default
                        >
                            {interactive => interactive.description}
                        </Column>
                        <Column
                            {...columnProps}
                            title="Link"
                            default
                        >
                            {interactive =>
                                <InteractiveDocument
                                    interactive={interactive}
                                    selectedAgency={selectedAgency}
                                />
                            }
                        </Column>
                    </>
                )}
            </PagedView>
            {isSearchBlock &&
            <InteractiveSearchForm
                formName='Interactive Forms'
                setIsSearchBlock={setIsSearchBlock}
                filters={interactiveFilters}
                filter={changeInteractiveFilters}
                interactiveFormsCategories={interactiveFormsCategories}
                interactiveFormsType={interactiveFormsType}
            />
            }
        </>
    );
};

export default InteractiveForm;
